import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { fetchAgencies } from '../../services/api';
import { toast } from 'react-toastify';

const API_URL = `${window.location.origin}/api`;

const UsersManagement = ({ users = [], updateUsers }) => {
  const { currentUser } = useAuth();
  const [usersData, setUsersData] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    agencyId: '',
    phone: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'user',
    agencyId: '',
    phone: ''
  });

  // Load users data
  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // If users were passed as props, use them
      if (Array.isArray(users) && users.length > 0) {
        setUsersData(users);
      } else {
        // Otherwise fetch users directly
        try {
          const response = await axios.get(`${API_URL}/users`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          setUsersData(Array.isArray(response.data) ? response.data : []);
          if (updateUsers) updateUsers(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
          console.error('Error fetching users:', err);
          setError('Failed to load users. Please try again later.');
        }
      }
      
      // Load agencies for the forms
      try {
        const agenciesData = await fetchAgencies();
        setAgencies(Array.isArray(agenciesData) ? agenciesData : []);
      } catch (err) {
        console.error('Error fetching agencies:', err);
      }
      
      setLastRefresh(new Date().toLocaleTimeString());
    } finally {
      setLoading(false);
    }
  };
  
  // Handle refresh button click
  const handleRefresh = () => {
    loadUsers();
    toast.info('Refreshing users data...');
  };

  // Load users data when component mounts
  useEffect(() => {
    loadUsers();
  }, [users]);

  // Handle role filter change
  const handleRoleFilterChange = (e) => {
    setFilterRole(e.target.value);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter users based on role and search term
  const filteredUsers = usersData.filter(user => {
    // Filter by role
    if (filterRole !== 'all' && user.role !== filterRole) {
      return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      return (
        (user.name && user.name.toLowerCase().includes(searchTermLower)) ||
        (user.email && user.email.toLowerCase().includes(searchTermLower))
      );
    }
    
    return true;
  });

  // Get role badge class based on role
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin':
        return 'role-super-admin';
      case 'admin':
        return 'role-admin';
      case 'agent_admin':
        return 'role-agent';
      case 'user':
      default:
        return 'role-user';
    }
  };

  // Format role for display
  const formatRole = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'agent_admin':
        return 'Agent Admin';
      case 'user':
        return 'User';
      default:
        return role;
    }
  };

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date) ? 'Invalid date' : date.toLocaleDateString();
  };

  // Start editing a user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'user',
      agencyId: user.agencyId || '',
      phone: user.phone || ''
    });
    setShowCreateForm(false);
  };

  // Handle form field changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value
    });
  };

  // Handle create form field changes
  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateForm({
      ...createForm,
      [name]: value
    });
  };

  // Toggle create form
  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
    if (!showCreateForm) {
      setEditingUser(null);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({
      name: '',
      email: '',
      role: 'user',
      agencyId: '',
      phone: ''
    });
  };

  // Cancel create
  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setCreateForm({
      name: '',
      email: '',
      password: '',
      role: 'user',
      agencyId: '',
      phone: ''
    });
  };

  // Create new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!createForm.name || !createForm.email || !createForm.password) {
      setError('Name, email and password are required fields');
      return;
    }
    
    try {
      setError('');
      setSuccessMessage('');
      
      const response = await axios.post(
        `${API_URL}/users`,
        createForm,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Add the new user to the list
      const updatedUsers = [...usersData, response.data];
      
      setUsersData(updatedUsers);
      if (updateUsers) updateUsers(updatedUsers);
      
      setSuccessMessage(`User ${response.data.name} created successfully`);
      
      // Reset the form
      setCreateForm({
        name: '',
        email: '',
        password: '',
        role: 'user',
        agencyId: '',
        phone: ''
      });
      
      setShowCreateForm(false);
      
      // Clear success message after a delay
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  // Save user changes
  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    try {
      setError('');
      setSuccessMessage('');
      
      const response = await axios.put(
        `${API_URL}/users/${editingUser._id}`,
        editForm,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Update the users list with the updated user
      const updatedUsers = usersData.map(user => 
        user._id === editingUser._id ? response.data : user
      );
      
      setUsersData(updatedUsers);
      if (updateUsers) updateUsers(updatedUsers);
      
      setSuccessMessage(`User ${response.data.name} updated successfully`);
      setEditingUser(null);
      
      // Clear success message after a delay
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  // Delete a user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      setError('');
      setSuccessMessage('');
      
      await axios.delete(`${API_URL}/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Remove the deleted user from the state
      const updatedUsers = usersData.filter(user => user._id !== userId);
      
      setUsersData(updatedUsers);
      if (updateUsers) updateUsers(updatedUsers);
      
      setSuccessMessage('User deleted successfully');
      
      // Clear success message after a delay
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  if (loading && usersData.length === 0) {
    return <div className="loading-indicator">Loading users data...</div>;
  }

  return (
    <div className="admin-content">
      <div className="admin-section">
        <div className="controls-row">
          <div className="title-section">
            <h3>Users Management</h3>
            {lastRefresh && (
              <div className="last-refresh">
                Last refreshed: {lastRefresh}
              </div>
            )}
          </div>
          
          <button 
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh Data
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}
        
        <div className="filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          <div className="role-filter">
            <label>Filter by Role:</label>
            <select
              value={filterRole}
              onChange={handleRoleFilterChange}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              <option value="user">Regular Users</option>
              <option value="agent_admin">Agent Admins</option>
              <option value="super_admin">Super Admins</option>
            </select>
          </div>

          {currentUser?.role === 'super_admin' && (
            <button
              className="btn-primary"
              onClick={toggleCreateForm}
            >
              {showCreateForm ? 'Cancel' : 'Create User'}
            </button>
          )}
        </div>

        {/* Create User Form */}
        {showCreateForm && currentUser?.role === 'super_admin' && (
          <div className="create-user-form admin-section">
            <h3>Create New User</h3>
            <form onSubmit={handleCreateUser}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="create-name">Name *</label>
                  <input
                    type="text"
                    id="create-name"
                    name="name"
                    value={createForm.name}
                    onChange={handleCreateFormChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="create-email">Email *</label>
                  <input
                    type="email"
                    id="create-email"
                    name="email"
                    value={createForm.email}
                    onChange={handleCreateFormChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="create-password">Password *</label>
                  <input
                    type="password"
                    id="create-password"
                    name="password"
                    value={createForm.password}
                    onChange={handleCreateFormChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="create-role">Role</label>
                  <select
                    id="create-role"
                    name="role"
                    value={createForm.role}
                    onChange={handleCreateFormChange}
                  >
                    <option value="user">User</option>
                    <option value="agent_admin">Agent Admin</option>
                    {currentUser?.role === 'super_admin' && (
                      <option value="super_admin">Super Admin</option>
                    )}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="create-phone">Phone</label>
                  <input
                    type="text"
                    id="create-phone"
                    name="phone"
                    value={createForm.phone}
                    onChange={handleCreateFormChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="create-agency">Agency</label>
                  <select
                    id="create-agency"
                    name="agencyId"
                    value={createForm.agencyId}
                    onChange={handleCreateFormChange}
                  >
                    <option value="">None</option>
                    {agencies.map(agency => (
                      <option key={agency._id} value={agency._id}>
                        {agency.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCancelCreate}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        )}
        
        {filteredUsers.length === 0 ? (
          <p>No users found matching your criteria.</p>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Agency</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user._id}>
                    <td>{user.name || 'N/A'}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {formatRole(user.role)}
                      </span>
                    </td>
                    <td>{user.agencyName || 'None'}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="edit-buttons">
                        <button 
                          className="btn-secondary"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        {currentUser?.role === 'super_admin' && user.role !== 'super_admin' && (
                          <button 
                            className="btn-secondary btn-delete"
                            onClick={() => handleDeleteUser(user._id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Edit User Form */}
        {editingUser && (
          <div className="edit-user-form">
            <h3>Edit User: {editingUser.name}</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditFormChange}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  name="role"
                  value={editForm.role}
                  onChange={handleEditFormChange}
                >
                  <option value="user">User</option>
                  <option value="agent_admin">Agent Admin</option>
                  {currentUser?.role === 'super_admin' && (
                    <option value="super_admin">Super Admin</option>
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="agencyId">Agency</label>
                <select
                  id="agencyId"
                  name="agencyId"
                  value={editForm.agencyId}
                  onChange={handleEditFormChange}
                >
                  <option value="">None</option>
                  {agencies.map(agency => (
                    <option key={agency._id} value={agency._id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={editForm.phone}
                  onChange={handleEditFormChange}
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveUser}
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="admin-section">
        <h3>User Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <h4>Total Users</h4>
            <p className="stat-number">{usersData.length}</p>
          </div>
          <div className="stat-card">
            <h4>Regular Users</h4>
            <p className="stat-number">
              {usersData.filter(user => user.role === 'user').length}
            </p>
          </div>
          <div className="stat-card">
            <h4>Agent Admins</h4>
            <p className="stat-number">
              {usersData.filter(user => user.role === 'agent_admin').length}
            </p>
          </div>
          <div className="stat-card">
            <h4>Super Admins</h4>
            <p className="stat-number">
              {usersData.filter(user => user.role === 'super_admin').length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement; 